import { resolveClinicNameForPatientEmail } from './loginRestrictions';

export const SMILECONSENT_REPLY_EMAIL = 'info@smileconsent.co.uk';
const FALLBACK_SENDER_NAME = 'Smile Consent';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOME_COUNTRIES = new Set(['uk', 'united kingdom', 'gb', 'great britain', 'england', 'scotland', 'wales', 'northern ireland']);

const clean = (value) => String(value ?? '').trim();

export const escapeHtml = (value) =>
  clean(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const normalisePracticeEmail = (value) => {
  const email = clean(value).toLowerCase();
  return EMAIL_PATTERN.test(email) ? email : '';
};

export const formatPracticeAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address.split(/\r?\n/).map(clean).filter(Boolean).join(', ');
  const country = clean(address.country);
  const parts = [address.street, address.city, address.state, address.zip].map(clean);
  if (country && !HOME_COUNTRIES.has(country.toLowerCase())) parts.push(country);
  return parts.filter(Boolean).join(', ');
};

/**
 * Practice identity for patient emails. `clinic` must be the clinics/{clinicId} record of the
 * practice that owns the consent; `fallbackName` is that same consent's stored clinic name.
 */
export const buildPracticeSender = (clinic, fallbackName = '') => {
  const name = resolveClinicNameForPatientEmail(clinic?.name, fallbackName);
  const contactEmail = normalisePracticeEmail(clinic?.email);
  const phone = clean(clinic?.phone);
  const address = formatPracticeAddress(clinic?.address);
  const displayName = name || FALLBACK_SENDER_NAME;

  const signatureLines = [
    `<strong style="color: #0f172a;">${escapeHtml(displayName)}</strong>`,
    address && escapeHtml(address),
    phone && `Tel: <a href="tel:${escapeHtml(phone.replace(/[^\d+]/g, ''))}" style="color: #2563eb; text-decoration: none;">${escapeHtml(phone)}</a>`,
    contactEmail && `Email: <a href="mailto:${escapeHtml(contactEmail)}" style="color: #2563eb; text-decoration: none;">${escapeHtml(contactEmail)}</a>`,
  ].filter(Boolean);

  return {
    name,
    displayName,
    replyToEmail: contactEmail || SMILECONSENT_REPLY_EMAIL,
    contactEmail,
    phone,
    address,
    replyNote: contactEmail
      ? `Questions? Reply to this email to contact ${displayName} directly.`
      : name
        ? `For help, please contact ${name} directly.`
        : 'For help, please contact your dental practice directly.',
    signatureHtml: signatureLines.join('<br>'),
  };
};

export const practiceTemplateParams = (practice) => ({
  practice_name: practice.displayName,
  practice_email: practice.replyToEmail,
  practice_contact_email: practice.contactEmail,
  practice_phone: practice.phone,
  practice_address: practice.address,
  practice_reply_note: practice.replyNote,
  practice_signature_html: practice.signatureHtml,
});

export const resolvePracticeSender = async ({ clinicId, clinicName } = {}) => {
  let clinic = null;
  if (clinicId) {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      const snap = await getDoc(doc(db, 'clinics', clinicId));
      if (snap.exists()) clinic = snap.data();
    } catch (e) {
      console.warn('[practiceSender] Could not load clinic record:', e?.message || e);
    }
  }
  return buildPracticeSender(clinic, clinicName);
};
